import { FunctionComponent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    compoundManagementSlice,
    getActiveCompoundManagementView,
    getLoadPreview,
    LoadPreview,
} from '../../../reducers/compoundManagementSlice';
import { MAX_BULK_SIZE } from './BulkStatusUpdateDialog';
import { useLoadPreviewSelection } from '../toolbar/StartBulkStatusUpdateButton';
import { runInBackground } from '../../../../../configuration/setup/backgroundActions';
import { FilterMaterializationExceededErrorResponseCodec } from '../../../api/types/compoundManagement/errorCodes';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { BulkStatusUpdateReviewTable } from './BulkStatusUpdateReviewTable';
import { BulkStatusVehicleMaxSizeErrorMessage } from './BulkStatusVehicleMaxSizeErrorMessage';
import { VinInput } from './BulkStatusUpdateVinInput';
import {
    LoadPreviewArrivals,
    LoadPreviewDepartures,
    LoadPreviewError,
    LoadPreviewErrorCode,
    LoadPreviewUnannouncedDepartures,
} from '../../compoundManagement.types';
import { parseUniqueEntries } from '../../../sharedComponents/inputListParser';
import {
    BulkStatusError,
    BulkStatusErrorMessage,
    getDefaultBulkStatusErrorMessageId,
} from '../../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';

// this is currently an arbitrary technical restriction in order to keep the payload for the request smaller
// if needed we can increase the limit and see if the performance is impacted
export const MAX_VINS_INPUT = 1000;

// eslint-disable-next-line max-lines-per-function
export const BulkStatusUpdateReview: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const currentCompoundView = useAppSelector(getActiveCompoundManagementView);
    const loadPreview = useAppSelector(getLoadPreview);

    const [vins, setVins] = useState('');
    const [limitExceededVinList, setLimitExceededVinList] = useState<undefined | string>();

    const { getCorrectValidateFunction, isLoading } = useLoadPreviewSelection(currentCompoundView);

    const vehiclesInLoadPreview = loadPreview?.vehicles ?? [];
    const invalidVehicles = loadPreview?.invalidVins ?? [];

    const deduplicatedVins = parseUniqueEntries(vins);
    const totalVehicleCount = vehiclesInLoadPreview.length + deduplicatedVins.length;
    const hasMalformedVin =
        deduplicatedVins.length !== 0 && deduplicatedVins.filter((txt) => txt.length !== 17).length !== 0;
    const overInputLimit = deduplicatedVins.length > MAX_VINS_INPUT;
    const vehicleMatchExceeded = vins === limitExceededVinList;
    const showMaxBulkSizeExceedMessage = totalVehicleCount > MAX_BULK_SIZE || vehicleMatchExceeded;
    const isVinButtonDisabled =
        hasMalformedVin || overInputLimit || deduplicatedVins.length === 0 || showMaxBulkSizeExceedMessage || isLoading;

    const addVins = () => {
        if (totalVehicleCount > MAX_BULK_SIZE) return;

        runInBackground(async () => {
            try {
                const validateLoad = getCorrectValidateFunction();
                if (validateLoad !== undefined) {
                    const result = await validateLoad({
                        ids: vehiclesInLoadPreview.map((v) => v.id) ?? [],
                        vins: deduplicatedVins,
                    }).unwrap();
                    const sortedResult = sortAddedVinsOnTop(deduplicatedVins, result);
                    dispatch(
                        compoundManagementSlice.actions.setLoadPreview({
                            view: currentCompoundView,
                            ...sortedResult,
                        } as LoadPreview),
                    );
                    setVins('');
                }
            } catch (error) {
                if (FilterMaterializationExceededErrorResponseCodec.is(error)) {
                    setLimitExceededVinList(vins);
                } else {
                    handleQueryError(error);
                }
            }
        });
    };

    const removeVehicle = (vehicleId: string) => {
        const newLoadPreview =
            loadPreview &&
            ({
                ...loadPreview,
                vehicles: vehiclesInLoadPreview.filter((v) => v.id !== vehicleId),
            } as LoadPreview);
        dispatch(compoundManagementSlice.actions.setLoadPreview(newLoadPreview));
    };

    return (
        <div>
            <BulkStatusUpdateReviewTable vehicles={vehiclesInLoadPreview} onRemove={removeVehicle} />
            <div className="border border-bottom-only margin-bottom-20" />
            <VinInput
                isLoading={isLoading}
                isButtonDisabled={isVinButtonDisabled}
                hasError={hasMalformedVin || overInputLimit}
                exceedsMaxVins={overInputLimit}
                vins={vins}
                setVins={setVins}
                addVins={addVins}
            />
            {showMaxBulkSizeExceedMessage && (
                <BulkStatusVehicleMaxSizeErrorMessage
                    totalCount={vehicleMatchExceeded ? undefined : totalVehicleCount}
                    maxBulkSize={MAX_BULK_SIZE}
                />
            )}
            {invalidVehicles.length > 0 && (
                <BulkStatusErrorMessage
                    errors={mapVehicleErrors(invalidVehicles)}
                    getErrorMessageId={getLoadPreviewErrorMessageId}
                />
            )}
        </div>
    );
};

const sortAddedVinsOnTop = (
    deduplicatedVins: string[],
    result: LoadPreviewArrivals | LoadPreviewDepartures | LoadPreviewUnannouncedDepartures,
) => {
    const vehicles = result.vehicles;
    const vehiclesAddedByVin = vehicles.filter((vehicle) => deduplicatedVins.includes(vehicle.vehicle.vin));
    const vehiclesWithoutVehiclesAddedByVin = vehicles.filter((vehicle) => !vehiclesAddedByVin.includes(vehicle));
    return {
        vehicles: [...vehiclesAddedByVin, ...vehiclesWithoutVehiclesAddedByVin],
        invalidVins: result.invalidVins,
        timeZone: result.timeZone,
    };
};

const mapVehicleErrors = (invalidVehicles: LoadPreviewError[]): BulkStatusError<LoadPreviewErrorCode>[] =>
    invalidVehicles.map((error) => ({
        errorCode: error.errorCode,
        identifiers: error.vins,
        identifierType: 'VEHICLE' as const,
    }));

export const getLoadPreviewErrorMessageId = (errorCode: LoadPreviewErrorCode) =>
    getDefaultBulkStatusErrorMessageId(errorCode);
